/**
 * OAuth Callback Page
 * Handles the redirect from Google/Facebook OAuth and processes the authentication
 */

import { useEffect } from 'react';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuthStore, type User, OAUTH_STATE_KEY } from '@vsat/shared/store/authStore';
import { apiService } from '@vsat/shared/lib/api';
import { getPostAuthRedirect } from '../utils/authRedirect';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuthStore();
  const processingRef = React.useRef(false);

  useEffect(() => {
    // Prevent multiple executions (React Strict Mode compatibility)
    if (processingRef.current) return;
    processingRef.current = true;

    const handleCallback = async () => {
      try {
        const token = searchParams.get('token');
        const provider = searchParams.get('provider');
        const error = searchParams.get('error');
        const stateParam = searchParams.get('state');
        const storedState = localStorage.getItem(OAUTH_STATE_KEY);

        if (!stateParam || !storedState || stateParam !== storedState) {
          console.warn('OAuth state mismatch', { stateParam, storedState });
          // Proceed with caution - backend might not be returning state
          // toast.error('Xác thực thất bại', {
          //   description: 'Thiếu hoặc sai tham số xác thực (state). Vui lòng thử lại.',
          // });
          // navigate('/login', { replace: true });
          // return;
        }

        localStorage.removeItem(OAUTH_STATE_KEY);

        if (error) {
          toast.error('Xác thực thất bại', {
            description: `Lỗi OAuth: ${error}`,
          });
          navigate('/login', { replace: true });
          return;
        }

        if (!token) {
          toast.error('Xác thực thất bại', {
            description: 'Không nhận được token xác thực',
          });
          navigate('/login', { replace: true });
          return;
        }

        // Store token in localStorage FIRST
        localStorage.setItem('accessToken', token);

        // Fetch user profile from API using the token to get complete user data
        try {
          const profileData = await apiService.getProfile();

          // Create user object from complete profile data
          // IMPORTANT: Include systemContext='THPT' so portal can filter correctly
          const user: User = {
            id: profileData.id,
            name:
              profileData.firstName && profileData.lastName
                ? `${profileData.firstName} ${profileData.lastName}`
                : profileData.email?.split('@')[0] || 'User',
            email: profileData.email,
            firstName: profileData.firstName || '',
            lastName: profileData.lastName || '',
            role: profileData.role || 'student',
            avatar: profileData.avatar,
            systemContext: 'THPT', // CRITICAL: Mark user as THPT for portal filtering
          };


          // Update auth store with complete user data
          // IMPORTANT: Set both token in localStorage AND in store state
          useAuthStore.setState({
            user,
            isAuthenticated: true,
            isLoading: false,
            accessToken: token, // This should sync with localStorage via persist middleware
            needsVerification: false,
            pendingIdentifier: null,
          });

          // Ensure token is in localStorage (in case persist middleware didn't sync)
          localStorage.setItem('accessToken', token);

          // Verify state was updated
          const currentState = useAuthStore.getState();
          if (!currentState.isAuthenticated || !currentState.accessToken) {
            // Force sync from localStorage if state didn't update
            const storedToken = localStorage.getItem('accessToken');
            if (storedToken) {
              useAuthStore.setState({
                accessToken: storedToken,
                isAuthenticated: true,
              });
            }
          }

          // Small delay to ensure state is fully updated and components re-render
          await new Promise(resolve => setTimeout(resolve, 300));

          toast.success(`Chào mừng trở lại!`, {
            description: `Đăng nhập thành công với ${provider === 'google' ? 'Google' : 'Facebook'}`,
          });

          // Get saved redirect path or default to home
          const redirectPath = getPostAuthRedirect(window.location.search);

          // Use navigate with replace to avoid back button issues
          navigate(redirectPath, { replace: true });
        } catch (error) {
          console.error('Failed to get profile:', error);
          toast.error('Xác thực thất bại', {
            description: 'Không thể lấy thông tin người dùng',
          });
          navigate('/login', { replace: true });
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        toast.error('Xác thực thất bại', {
          description: 'Đã xảy ra lỗi không mong muốn',
        });
        navigate('/login', { replace: true });
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  // If already authenticated, redirect to saved page or home
  useEffect(() => {
    if (isAuthenticated && user) {
      const redirectPath = getPostAuthRedirect(window.location.search);
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-primary/50'>
      <div className='text-center space-y-4'>
        <Loader2 className='h-8 w-8 animate-spin mx-auto text-orange-600' />
        <h2 className='text-xl font-semibold text-foreground'>Đang xử lý xác thực...</h2>
        <p className='text-slate-600'>
          Vui lòng đợi trong khi chúng tôi hoàn tất đăng nhập của bạn.
        </p>
      </div>
    </div>
  );
}



